import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  fetchFarms,  createFarm,  updateFarm,  deleteFarm,
  fetchZones,  createZone,  updateZone,  deleteZone,
  fetchGHSEs,  createGHSE,  updateGHSE,  deleteGHSE,
  fetchBays,   createBay,   updateBay,   deleteBay,
} from "../api/ghseApi";
import "../styles/ManageGHSE.css";

/* ══════════════════════════════════════════════
   Toast hook
══════════════════════════════════════════════ */
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  };
  return { toasts, add };
};

/* ══════════════════════════════════════════════
   Confirm hook + dialog
══════════════════════════════════════════════ */
const useConfirm = () => {
  const [state, setState] = useState(null);
  const confirm = useCallback(
    (opts) => new Promise((resolve) => setState({ ...opts, resolve })),
    []
  );
  const handleClose = (result) => { state?.resolve(result); setState(null); };
  return { confirm, confirmState: state, handleClose };
};

const ConfirmDialog = ({ state, onClose }) => {
  const overlayRef = useRef(null);
  useEffect(() => {
    if (!state) return;
    const h = (e) => { if (e.key === "Escape") onClose(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [state, onClose]);

  if (!state) return null;
  const {
    title = "Are you sure?",
    message = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "danger",
    icon = null,
  } = state;

  return (
    <div
      className="cd-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(false); }}
    >
      <div className={`cd-dialog cd-dialog--${variant}`} role="alertdialog" aria-modal="true">
        <div className={`cd-dialog-stripe cd-dialog-stripe--${variant}`} />
        <div className="cd-dialog-body">
          <div className={`cd-dialog-iconring cd-dialog-iconring--${variant}`}>
            {icon || (variant === "danger" ? "⚠" : "ℹ")}
          </div>
          <div className="cd-dialog-text">
            <p className="cd-dialog-title">{title}</p>
            {message && <p className="cd-dialog-msg">{message}</p>}
          </div>
        </div>
        <div className="cd-dialog-actions">
          <button className="cd-btn cd-btn--ghost" onClick={() => onClose(false)} autoFocus>
            {cancelLabel}
          </button>
          <button className={`cd-btn cd-btn--${variant}`} onClick={() => onClose(true)}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   Stat pill
══════════════════════════════════════════════ */
const StatPill = ({ icon, value, label, active }) => (
  <div className={`stat-pill${active ? " stat-pill--active" : ""}`}>
    <span className="stat-pill-icon">{icon}</span>
    <span className="stat-pill-value">{value}</span>
    <span className="stat-pill-label">{label}</span>
  </div>
);

/* ══════════════════════════════════════════════
   Breadcrumb trail
══════════════════════════════════════════════ */
const Breadcrumb = ({ farm, zone, ghse, onCrumbClick }) => {
  const crumbs = [
    { label: "All Farms", active: !farm, level: 0 },
    farm && { label: farm.name, active: !zone, level: 1, id: farm.id },
    zone && { label: zone.name, active: !ghse, level: 2, id: zone.id },
    ghse && { label: ghse.name, active: true, level: 3, id: ghse.id },
  ].filter(Boolean);

  return (
    <nav className="breadcrumb" aria-label="Hierarchy path">
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="breadcrumb-sep">›</span>}
          <button
            className={`breadcrumb-item${c.active ? " breadcrumb-item--active" : ""}`}
            onClick={() => onCrumbClick?.(c.level, c.id)}
            disabled={c.active}
            type="button"
          >
            {c.label}
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
};

/* ══════════════════════════════════════════════
   Level stepper (sidebar header)
══════════════════════════════════════════════ */
const LevelBadge = ({ num, label, done, current }) => (
  <div className={`level-badge${done ? " level-badge--done" : current ? " level-badge--current" : ""}`}>
    <span className="level-badge-num">{done ? "✓" : num}</span>
    <span className="level-badge-label">{label}</span>
  </div>
);

/* ══════════════════════════════════════════════
   Inline Edit Input
══════════════════════════════════════════════ */
const InlineEdit = ({ value, onSave, onCancel, placeholder }) => {
  const [val, setVal] = useState(value);
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const submit = () => {
    const trimmed = val.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else onCancel();
  };

  return (
    <input
      ref={ref}
      className="sp-input sp-input--inline"
      value={val}
      onChange={e => setVal(e.target.value)}
      onKeyDown={e => {
        if (e.key === "Enter") submit();
        if (e.key === "Escape") onCancel();
      }}
      onBlur={submit}
      placeholder={placeholder}
    />
  );
};

/* ══════════════════════════════════════════════
   Selector Panel
══════════════════════════════════════════════ */
const SelectorPanel = ({
  num, title, icon, color, items, selectedId,
  onSelect, onDelete, onAdd, onEdit,
  placeholder, isLocked, lockedMsg, supportsUpperLower = false, onToggleUpperLower,
}) => {
  const [val, setVal]   = useState("");
  const [newUpperLower, setNewUpperLower] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const inputRef        = useRef(null);
  const isDone          = items.length > 0 && !!selectedId;
  const isCurrent       = !isLocked && !isDone;

  const submit = async () => {
    if (!val.trim() || busy) return;
    setBusy(true);
    await onAdd(val.trim(), supportsUpperLower ? newUpperLower : undefined);
    setBusy(false);
    setVal("");
    setNewUpperLower(false);
    inputRef.current?.focus();
  };

  const handleEdit = async (id, newName) => {
    setEditingId(null);
    if (!newName) return;
    await onEdit(id, newName);
  };

  return (
    <div className={`sp-card${isLocked ? " sp-card--locked" : ""}`} style={{ "--sp-accent": color }}>
      <div className="sp-head">
        <LevelBadge num={num} label={`Level 0${num}`} done={isDone} current={isCurrent} />
        <div className="sp-head-right">
          <span className="sp-icon">{icon}</span>
          <span className="sp-title">{title}</span>
          {!isLocked && <span className="sp-count">{items.length}</span>}
        </div>
      </div>

      {isLocked ? (
        <div className="sp-locked">
          <span className="sp-locked-arrow">↑</span>
          {lockedMsg}
        </div>
      ) : (
        <>
          <div className="sp-add">
            <input
              ref={inputRef}
              className="sp-input"
              placeholder={placeholder}
              value={val}
              onChange={e => setVal(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              disabled={busy}
            />
            <button
              className="sp-addbtn"
              onClick={submit}
              disabled={busy || !val.trim()}
              title={`Add ${title.replace(/s$/, "")}`}
            >
              {busy ? "…" : "+"}
            </button>
          </div>
          {supportsUpperLower && <label className={`ghse-capture-option${newUpperLower ? " is-enabled" : ""}`}><input type="checkbox" checked={newUpperLower} onChange={e => setNewUpperLower(e.target.checked)} disabled={busy}/><span className="ghse-option-icon" aria-hidden="true"><i>U</i><i>L</i></span><span className="ghse-option-copy"><strong>Separate upper &amp; lower harvest</strong><small>Ask the harvester to choose a level for each capture.</small></span><span className="ghse-option-indicator" aria-hidden="true">{newUpperLower ? "✓" : "＋"}</span></label>}

          {items.length === 0 ? (
            <p className="sp-empty">No {title.toLowerCase()} yet</p>
          ) : (
            <ul className="sp-list">
              {items.map(item => (
                <li
                  key={item.id}
                  className={`sp-item${selectedId === item.id ? " sp-item--sel" : ""}`}
                  onClick={() => onSelect?.(item.id)}
                >
                  <span className="sp-item-dot" />
                  {editingId === item.id ? (
                    <InlineEdit
                      value={item.name}
                      onSave={(name) => handleEdit(item.id, name)}
                      onCancel={() => setEditingId(null)}
                      placeholder={`Rename ${title.toLowerCase()}`}
                    />
                  ) : (
                    <span className="sp-item-name">{item.name}</span>
                  )}
                  {supportsUpperLower && <span className={`ghse-level-badge ${item.requires_upper_lower ? "is-split" : ""}`}>{item.requires_upper_lower ? "Upper / Lower" : "Single level"}</span>}
                  <div className="sp-item-actions">
                    {supportsUpperLower && <button className="sp-item-edit ghse-level-toggle" title={item.requires_upper_lower ? `Disable level capture for ${item.name}` : `Enable upper/lower capture for ${item.name}`} onClick={e => { e.stopPropagation(); onToggleUpperLower?.(item); }}>{item.requires_upper_lower ? "↕" : "⇵"}</button>}
                    <button
                      className="sp-item-edit"
                      title={`Edit ${item.name}`}
                      onClick={e => { e.stopPropagation(); setEditingId(item.id); }}
                    >
                      ✎
                    </button>
                    <button
                      className="sp-item-del"
                      title={`Delete ${item.name}`}
                      onClick={e => { e.stopPropagation(); onDelete(item.id); }}
                    >
                      ×
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   Data Table Card (main area)
══════════════════════════════════════════════ */
const DataCard = ({
  icon, color, title, subtitle, items,
  onDelete, onAdd, onEdit, addPlaceholder,
  contextLabel, isLocked, lockedMsg, supportsUpperLower = false, onToggleUpperLower,
}) => {
  const [val, setVal]   = useState("");
  const [newUpperLower, setNewUpperLower] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const inputRef        = useRef(null);

  const submit = async () => {
    if (!val.trim() || isLocked || busy) return;
    setBusy(true);
    await onAdd(val.trim(), supportsUpperLower ? newUpperLower : undefined);
    setBusy(false);
    setVal("");
    setNewUpperLower(false);
    inputRef.current?.focus();
  };

  const handleEdit = async (id, newName) => {
    setEditingId(null);
    if (!newName) return;
    await onEdit(id, newName);
  };

  if (isLocked) {
    return (
      <div className="dc-locked" style={{ "--dc-color": color }}>
        <span className="dc-locked-icon">{icon}</span>
        <p className="dc-locked-title">{lockedMsg}</p>
        <p className="dc-locked-hint">Complete the step in the sidebar to unlock.</p>
      </div>
    );
  }

  return (
    <div className="dc-card" style={{ "--dc-color": color }}>
      <div className="dc-head">
        <div className="dc-head-left">
          <span className="dc-head-icon">{icon}</span>
          <div>
            <h3 className="dc-title">{title}</h3>
            {subtitle && <p className="dc-subtitle">{subtitle}</p>}
          </div>
        </div>
        <span className="dc-badge">{items.length} records</span>
      </div>

      <div className="dc-add">
        <input
          ref={inputRef}
          className="dc-input"
          placeholder={addPlaceholder}
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          disabled={busy}
        />
        <button
          className="dc-addbtn"
          onClick={submit}
          disabled={busy || !val.trim()}
        >
          {busy ? "Adding…" : `+ Add ${title.replace(/s$/, "")}`}
        </button>
      </div>
      {supportsUpperLower && <label className={`ghse-capture-option ghse-capture-option--table${newUpperLower ? " is-enabled" : ""}`}><input type="checkbox" checked={newUpperLower} onChange={e => setNewUpperLower(e.target.checked)} disabled={busy}/><span className="ghse-option-icon" aria-hidden="true"><i>U</i><i>L</i></span><span className="ghse-option-copy"><strong>Separate upper &amp; lower harvest</strong><small>Ask the harvester to choose a level for each capture.</small></span><span className="ghse-option-indicator" aria-hidden="true">{newUpperLower ? "✓" : "＋"}</span></label>}

      <div className="dc-table-wrap">
        <table className="dc-table">
          <thead>
            <tr>
              <th className="dc-th dc-th--num">#</th>
              <th className="dc-th">Name</th>
              <th className="dc-th">Context</th>
              <th className="dc-th dc-th--action">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <div className="dc-empty">
                    <span className="dc-empty-icon">{icon}</span>
                    <p>No {title.toLowerCase()} added yet. Use the form above.</p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item, i) => (
                <tr key={item.id} className="dc-row">
                  <td className="dc-td dc-td--num">{String(i + 1).padStart(2, "0")}</td>
                  <td className="dc-td dc-td--name">
                    {editingId === item.id ? (
                      <InlineEdit
                        value={item.name}
                        onSave={(name) => handleEdit(item.id, name)}
                        onCancel={() => setEditingId(null)}
                        placeholder="New name…"
                      />
                    ) : (
                      item.name
                    )}
                  </td>
                  <td className="dc-td">
                    <span className="dc-ctx-tag">{contextLabel}</span>
                    {supportsUpperLower && <span className={`ghse-level-badge ${item.requires_upper_lower ? "is-split" : ""}`}>{item.requires_upper_lower ? "Upper / Lower" : "Single level"}</span>}
                  </td>
                  <td className="dc-td dc-td--action">
                    <button
                      className="dc-edit"
                      onClick={() => setEditingId(item.id)}
                      title={`Edit ${item.name}`}
                    >
                      Edit
                    </button>
                    {supportsUpperLower && <button className="dc-edit ghse-level-toggle-table" type="button" onClick={() => onToggleUpperLower?.(item)}>{item.requires_upper_lower ? "Disable levels" : "Enable levels"}</button>}
                    <button
                      className="dc-del"
                      onClick={() => onDelete(item.id)}
                      title={`Remove ${item.name}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   Root Component
══════════════════════════════════════════════ */
const ManageGHSE = () => {
  const [farms,  setFarms]  = useState([]);
  const [zones,  setZones]  = useState([]);
  const [ghses,  setGHSEs]  = useState([]);
  const [bays,   setBays]   = useState([]);

  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedGHSE, setSelectedGHSE] = useState(null);

  const { toasts, add: toast }            = useToast();
  const { confirm, confirmState, handleClose } = useConfirm();

  /* Data fetching */
  useEffect(() => { fetchFarms().then(setFarms); }, []);

  useEffect(() => {
    if (!selectedFarm) return;
    fetchZones(selectedFarm).then(setZones);
    setSelectedZone(null); setSelectedGHSE(null); setGHSEs([]); setBays([]);
  }, [selectedFarm]);

  useEffect(() => {
    if (!selectedZone) return;
    fetchGHSEs(selectedFarm, selectedZone).then(setGHSEs);
    setSelectedGHSE(null); setBays([]);
  }, [selectedZone]);

  useEffect(() => {
    if (!selectedGHSE) return;
    fetchBays(selectedGHSE).then(setBays);
  }, [selectedGHSE]);

  /* ── Add handlers ── */
  const handleAddFarm = async (name) => {
    const ok = await confirm({ title: "Create Farm", message: `Add a new farm named "${name}"?`, confirmLabel: "Create", cancelLabel: "Cancel", variant: "info", icon: "🌾" });
    if (!ok) return;
    const r = await createFarm({ name }); setFarms(p => [...p, r]); toast(`Farm "${name}" created`);
  };
  const handleAddZone = async (name) => {
    if (!selectedFarm) return;
    const farmName = farms.find(f => f.id === selectedFarm)?.name;
    const ok = await confirm({ title: "Create Zone", message: `Add zone "${name}" to farm "${farmName}"?`, confirmLabel: "Create", cancelLabel: "Cancel", variant: "info", icon: "🗺️" });
    if (!ok) return;
    const r = await createZone({ name, farm: selectedFarm }); setZones(p => [...p, r]); toast(`Zone "${name}" created`);
  };
  const handleAddGHSE = async (name, requiresUpperLower = false) => {
    if (!selectedFarm || !selectedZone) return;
    const zoneName = zones.find(z => z.id === selectedZone)?.name;
    const levelNote = requiresUpperLower ? " Harvest captures will be tagged Upper or Lower." : "";
    const ok = await confirm({ title: "Create GHSE", message: `Add GHSE "${name}" to zone "${zoneName}"?${levelNote}`, confirmLabel: "Create", cancelLabel: "Cancel", variant: "info", icon: "🏗️" });
    if (!ok) return;
    const r = await createGHSE({ name, farm: selectedFarm, zone: selectedZone, requires_upper_lower: Boolean(requiresUpperLower) }); setGHSEs(p => [...p, r]); toast(`GHSE "${name}" created`);
  };
  const handleToggleGHSEUpperLower = async (ghse) => {
    const next = !ghse.requires_upper_lower;
    const r = await updateGHSE(ghse.id, { requires_upper_lower: next });
    setGHSEs(current => current.map(item => item.id === ghse.id ? r : item));
    toast(`${ghse.name}: ${next ? "Upper/lower capture enabled" : "Upper/lower capture disabled"}`);
  };
  const handleAddBay = async (name) => {
    if (!selectedGHSE) return;
    const ghseName = ghses.find(g => g.id === selectedGHSE)?.name;
    const ok = await confirm({ title: "Create Bay", message: `Add bay "${name}" to GHSE "${ghseName}"?`, confirmLabel: "Create", cancelLabel: "Cancel", variant: "info", icon: "🚪" });
    if (!ok) return;
    const r = await createBay({ name, ghse: selectedGHSE }); setBays(p => [...p, r]); toast(`Bay "${name}" created`);
  };

  /* ── Edit handlers (NOW WIRED TO REAL API) ── */
  const handleEditFarm = async (id, name) => {
    const old = farms.find(f => f.id === id);
    const ok = await confirm({ title: "Update Farm", message: `Rename "${old.name}" to "${name}"?`, confirmLabel: "Update", cancelLabel: "Cancel", variant: "info", icon: "🌾" });
    if (!ok) return;
    const r = await updateFarm(id, { name });
    setFarms(p => p.map(f => f.id === id ? r : f));
    toast(`Farm renamed to "${name}"`);
  };
  const handleEditZone = async (id, name) => {
    const old = zones.find(z => z.id === id);
    const ok = await confirm({ title: "Update Zone", message: `Rename "${old.name}" to "${name}"?`, confirmLabel: "Update", cancelLabel: "Cancel", variant: "info", icon: "🗺️" });
    if (!ok) return;
    const r = await updateZone(id, { name });
    setZones(p => p.map(z => z.id === id ? r : z));
    toast(`Zone renamed to "${name}"`);
  };
  const handleEditGHSE = async (id, name) => {
    const old = ghses.find(g => g.id === id);
    const ok = await confirm({ title: "Update GHSE", message: `Rename "${old.name}" to "${name}"?`, confirmLabel: "Update", cancelLabel: "Cancel", variant: "info", icon: "🏗️" });
    if (!ok) return;
    const r = await updateGHSE(id, { name });
    setGHSEs(p => p.map(g => g.id === id ? r : g));
    toast(`GHSE renamed to "${name}"`);
  };
  const handleEditBay = async (id, name) => {
    const old = bays.find(b => b.id === id);
    const ok = await confirm({ title: "Update Bay", message: `Rename "${old.name}" to "${name}"?`, confirmLabel: "Update", cancelLabel: "Cancel", variant: "info", icon: "🚪" });
    if (!ok) return;
    const r = await updateBay(id, { name });
    setBays(p => p.map(b => b.id === id ? r : b));
    toast(`Bay renamed to "${name}"`);
  };

  /* ── Delete handlers ── */
  const handleDelFarm = async (id) => {
    const n = farms.find(f => f.id === id)?.name;
    const ok = await confirm({ title: "Delete Farm", message: `Delete "${n}"? All zones, GHSEs and bays inside will be removed permanently.`, confirmLabel: "Delete", cancelLabel: "Keep it", variant: "danger", icon: "🌾" });
    if (!ok) return;
    await deleteFarm(id); setFarms(p => p.filter(x => x.id !== id));
    if (selectedFarm === id) { setSelectedFarm(null); setZones([]); setGHSEs([]); setBays([]); }
    toast(`Farm "${n}" deleted`, "error");
  };
  const handleDelZone = async (id) => {
    const n = zones.find(z => z.id === id)?.name;
    const ok = await confirm({ title: "Delete Zone", message: `Delete zone "${n}"? All GHSEs and bays inside will be removed.`, confirmLabel: "Delete", cancelLabel: "Keep it", variant: "danger", icon: "🗺️" });
    if (!ok) return;
    await deleteZone(id); setZones(p => p.filter(x => x.id !== id));
    if (selectedZone === id) { setSelectedZone(null); setGHSEs([]); setBays([]); }
    toast(`Zone "${n}" deleted`, "error");
  };
  const handleDelGHSE = async (id) => {
    const n = ghses.find(g => g.id === id)?.name;
    const ok = await confirm({ title: "Delete GHSE", message: `Delete "${n}"? All bays inside will be removed.`, confirmLabel: "Delete", cancelLabel: "Keep it", variant: "danger", icon: "🏗️" });
    if (!ok) return;
    await deleteGHSE(id); setGHSEs(p => p.filter(x => x.id !== id));
    if (selectedGHSE === id) { setSelectedGHSE(null); setBays([]); }
    toast(`GHSE "${n}" deleted`, "error");
  };
  const handleDelBay = async (id) => {
    const n = bays.find(b => b.id === id)?.name;
    const ok = await confirm({ title: "Delete Bay", message: `Delete bay "${n}"? This cannot be undone.`, confirmLabel: "Delete", cancelLabel: "Keep it", variant: "danger", icon: "🚪" });
    if (!ok) return;
    await deleteBay(id); setBays(p => p.filter(x => x.id !== id));
    toast(`Bay "${n}" deleted`, "error");
  };

  /* ── Breadcrumb navigation ── */
  const handleCrumbClick = (level) => {
    if (level === 0) { setSelectedFarm(null); setSelectedZone(null); setSelectedGHSE(null); }
    if (level === 1) { setSelectedZone(null); setSelectedGHSE(null); }
    if (level === 2) { setSelectedGHSE(null); }
  };

  const farmObj = farms.find(f => f.id === selectedFarm);
  const zoneObj = zones.find(z => z.id === selectedZone);
  const ghseObj = ghses.find(g => g.id === selectedGHSE);

  return (
    <div className="mg-root">
      <ConfirmDialog state={confirmState} onClose={handleClose} />

      {/* Toast stack */}
      <div className="toast-stack" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast--${t.type}`}>
            <span className="toast-icon">{t.type === "success" ? "✓" : "✕"}</span>
            {t.msg}
          </div>
        ))}
      </div>

      {/* Page */}
      <div className="mg-page">

        {/* Header */}
        <header className="mg-header">
          <div className="mg-header-content">
            <div className="mg-logo">
              <div className="mg-logo-mark">GH</div>
              <div>
                <h1 className="mg-title">Manage GHSE</h1>
                <p className="mg-tagline">Farm Infrastructure Hierarchy</p>
              </div>
            </div>
            <div className="mg-stats-row">
              <StatPill icon="🌾" value={farms.length}  label="Farms"  active={!!selectedFarm} />
              <StatPill icon="🗺️" value={zones.length}  label="Zones"  active={!!selectedZone} />
              <StatPill icon="🏗️" value={ghses.length}  label="GHSEs"  active={!!selectedGHSE} />
              <StatPill icon="🚪" value={bays.length}   label="Bays"   active={bays.length > 0} />
            </div>
          </div>
          <Breadcrumb farm={farmObj} zone={zoneObj} ghse={ghseObj} onCrumbClick={handleCrumbClick} />
        </header>

        {/* Body */}
        <div className="mg-body">

          {/* Sidebar */}
          <aside className="mg-sidebar">
            <p className="mg-sidebar-label">Build Hierarchy</p>
            <SelectorPanel
              num={1} title="Farms" icon="🌾" color="var(--c-green)"
              items={farms} selectedId={selectedFarm}
              onSelect={setSelectedFarm} onDelete={handleDelFarm} onAdd={handleAddFarm} onEdit={handleEditFarm}
              placeholder="e.g. Sunrise Farm"
            />
            <SelectorPanel
              num={2} title="Zones" icon="🗺️" color="var(--c-teal)"
              items={zones} selectedId={selectedZone}
              onSelect={setSelectedZone} onDelete={handleDelZone} onAdd={handleAddZone} onEdit={handleEditZone}
              placeholder="e.g. North Zone"
              isLocked={!selectedFarm} lockedMsg="Select a farm first"
            />
            <SelectorPanel
              num={3} title="GHSEs" icon="🏗️" color="var(--c-amber)"
              items={ghses} selectedId={selectedGHSE}
              onSelect={setSelectedGHSE} onDelete={handleDelGHSE} onAdd={handleAddGHSE} onEdit={handleEditGHSE}
              placeholder="e.g. GHSE A1"
              supportsUpperLower onToggleUpperLower={handleToggleGHSEUpperLower}
              isLocked={!selectedZone} lockedMsg="Select a zone first"
            />
          </aside>

          {/* Main tables */}
          <main className="mg-main">
            <DataCard
              icon="🗺️" color="var(--c-teal)"
              title="Zones"
              subtitle={farmObj ? `Zones within ${farmObj.name}` : undefined}
              items={zones}
              onDelete={handleDelZone} onAdd={handleAddZone} onEdit={handleEditZone}
              addPlaceholder="New zone name…"
              contextLabel={farmObj?.name || "—"}
              isLocked={!selectedFarm}
              lockedMsg="Select a Farm to manage Zones"
            />
            <DataCard
              icon="🏗️" color="var(--c-amber)"
              title="GHSEs"
              subtitle={zoneObj ? `GHSEs within ${zoneObj.name}` : undefined}
              items={ghses}
              onDelete={handleDelGHSE} onAdd={handleAddGHSE} onEdit={handleEditGHSE}
              addPlaceholder="New GHSE name…"
              contextLabel={zoneObj?.name || "—"}
              supportsUpperLower onToggleUpperLower={handleToggleGHSEUpperLower}
              isLocked={!selectedZone}
              lockedMsg="Select a Zone to manage GHSEs"
            />
            <DataCard
              icon="🚪" color="var(--c-rose)"
              title="Bays"
              subtitle={ghseObj ? `Bays within ${ghseObj.name}` : undefined}
              items={bays}
              onDelete={handleDelBay} onAdd={handleAddBay} onEdit={handleEditBay}
              addPlaceholder="New bay name…"
              contextLabel={ghseObj?.name || "—"}
              isLocked={!selectedGHSE}
              lockedMsg="Select a GHSE to manage Bays"
            />
          </main>

        </div>
      </div>
    </div>
  );
};

export default ManageGHSE;
